// Citation generator - build MLA 9 / APA 7 references for the common source
// types students actually cite (website, book, journal article), copy one, or
// keep a running Works Cited / References list (saved locally) and copy it all.
import { el, btn, input, wrap, sel, field, note, copy as copyText, announce, saveFlag } from "./ui.js";
import { readJSON, writeJSON } from "../shared/store.js";

const KEY = "revault:citations";
const load = () => { const v = readJSON(KEY, []); return Array.isArray(v) ? v : []; };

// Field sets per source type. Each is [key, label, placeholder].
const FIELDS = {
	website: [
		["author", "Author (Last, First)", "Doe, Jane"],
		["title", "Page title", "How Photosynthesis Works"],
		["site", "Website / publisher", "National Geographic"],
		["year", "Year", "2023"],
		["month", "Month Day (optional)", "March 4"],
		["url", "URL", "https://example.com/article"],
		["accessed", "Date accessed (MLA)", "10 May 2024"],
	],
	book: [
		["author", "Author (Last, First)", "Orwell, George"],
		["title", "Book title", "Nineteen Eighty-Four"],
		["publisher", "Publisher", "Secker & Warburg"],
		["year", "Year", "1949"],
	],
	journal: [
		["author", "Author (Last, First)", "Smith, John"],
		["title", "Article title", "Cell Division in Plants"],
		["journal", "Journal name", "Journal of Botany"],
		["volume", "Volume", "12"],
		["issue", "Issue", "3"],
		["year", "Year", "2020"],
		["pages", "Pages", "45-59"],
	],
};

const has = (v) => v && String(v).trim().length > 0;
const dot = (s) => (/[.!?]$/.test(s.trim()) ? s.trim() : s.trim() + ".");
// "Last, First" -> "First Last" (for APA we keep initials; MLA keeps full).
function apaAuthor(a) {
	if (!has(a)) return "";
	const m = a.split(",");
	if (m.length < 2) return dot(a);
	const last = m[0].trim();
	const first = m[1].trim();
	const initials = first.split(/\s+/).filter(Boolean).map((w) => w[0].toUpperCase() + ".").join(" ");
	return last + ", " + initials;
}

function mla(type, f) {
	const parts = [];
	if (has(f.author)) parts.push(dot(f.author));
	if (type === "website") {
		if (has(f.title)) parts.push("“" + f.title.trim() + ".”");
		if (has(f.site)) parts.push(italic(f.site) + ",");
		const date = [f.month, f.year].filter(has).map((x) => x.trim()).join(" ");
		if (date) parts.push(date + ",");
		if (has(f.url)) parts.push(f.url.trim().replace(/^https?:\/\//, "") + ".");
		if (has(f.accessed)) parts.push("Accessed " + f.accessed.trim() + ".");
	} else if (type === "book") {
		if (has(f.title)) parts.push(italic(f.title) + ".");
		if (has(f.publisher)) parts.push(f.publisher.trim() + ",");
		if (has(f.year)) parts.push(f.year.trim() + ".");
	} else {
		if (has(f.title)) parts.push("“" + f.title.trim() + ".”");
		if (has(f.journal)) {
			let j = italic(f.journal);
			if (has(f.volume)) j += ", vol. " + f.volume.trim();
			if (has(f.issue)) j += ", no. " + f.issue.trim();
			if (has(f.year)) j += ", " + f.year.trim();
			if (has(f.pages)) j += ", pp. " + f.pages.trim();
			parts.push(j + ".");
		}
	}
	return parts.join(" ").replace(/\s+/g, " ").trim();
}

function apa(type, f) {
	const parts = [];
	const auth = apaAuthor(f.author);
	if (auth) parts.push(auth);
	if (has(f.year)) parts.push("(" + f.year.trim() + ").");
	if (type === "website") {
		if (has(f.title)) parts.push(dot(f.title));
		if (has(f.site)) parts.push(italic(f.site) + ".");
		if (has(f.url)) parts.push(f.url.trim());
	} else if (type === "book") {
		if (has(f.title)) parts.push(italic(f.title) + ".");
		if (has(f.publisher)) parts.push(dot(f.publisher));
	} else {
		if (has(f.title)) parts.push(dot(f.title));
		if (has(f.journal)) {
			let j = italic(f.journal);
			if (has(f.volume)) j += ", " + italic(f.volume);
			if (has(f.issue)) j += "(" + f.issue.trim() + ")";
			if (has(f.pages)) j += ", " + f.pages.trim();
			parts.push(j + ".");
		}
	}
	return parts.join(" ").replace(/\s+/g, " ").trim();
}

// Italics are represented with a marker so the on-screen preview can show them
// while the copied plain text stays clean (plain text can't carry italics).
function italic(s) { return "⁣" + s.trim() + "⁣"; }
function toHtml(s, esc) { return esc(s).split("⁣").map((seg, i) => (i % 2 ? "<i>" + seg + "</i>" : seg)).join(""); }
function toPlain(s) { return s.split("⁣").join(""); }

export function cite(root) {
	const s = wrap(root); s.classList.add("rv-cite");
	let saved = load();
	const esc = (t) => { const d = document.createElement("div"); d.textContent = t == null ? "" : String(t); return d.innerHTML; };

	const bar = el("div", "rv-trow");
	const styleSel = sel([["mla", "MLA 9"], ["apa", "APA 7"]], "mla");
	const typeSel = sel([["website", "Website"], ["book", "Book"], ["journal", "Journal article"]], "website");
	bar.append(field("Style", styleSel), field("Source type", typeSel));
	s.appendChild(bar);

	const formWrap = el("div", "rv-cite-fields");
	s.appendChild(formWrap);

	const outWrap = el("div", "rv-cite-out");
	const outLabel = el("div", "rv-field-label", "Preview");
	const out = el("div", "rv-cite-preview");
	out.setAttribute("aria-live", "polite");
	const outActions = el("div", "rv-trow");
	const flag = saveFlag();
	const copyBtn = btn("Copy", () => { copyText(toPlain(current())); announce("Citation copied"); }, "rv-tbtn");
	const addBtn = btn("Add to list", () => { const c = current(); if (!toPlain(c).trim()) return; saved.push({ id: Date.now(), style: styleSel.value, text: c }); flag.set(writeJSON(KEY, saved)); renderSaved(); }, "rv-tbtn rv-tbtn-accent");
	outActions.append(copyBtn, addBtn);
	outWrap.append(outLabel, out, outActions, flag);
	s.appendChild(outWrap);

	const savedWrap = el("div", "rv-cite-saved");
	s.appendChild(savedWrap);

	let inputs = {};
	function buildFields() {
		formWrap.textContent = ""; inputs = {};
		FIELDS[typeSel.value].forEach(([k, label, ph]) => {
			const i = input("text", { placeholder: ph });
			i.addEventListener("input", update);
			inputs[k] = i;
			formWrap.appendChild(field(label, i));
		});
		update();
	}
	function values() { const f = {}; Object.keys(inputs).forEach((k) => (f[k] = inputs[k].value)); return f; }
	function current() { const gen = styleSel.value === "apa" ? apa : mla; return gen(typeSel.value, values()); }
	function update() {
		const c = current();
		out.innerHTML = toPlain(c).trim() ? toHtml(c, esc) : '<span class="rv-tnote">Fill in the fields above to build a citation.</span>';
	}
	function renderSaved() {
		savedWrap.textContent = "";
		if (!saved.length) return;
		const head = el("div", "rv-cite-saved-head");
		head.append(el("span", "rv-tool-sub", saved.length === 1 ? "Works Cited (1)" : "Works Cited (" + saved.length + ")"),
			btn("Copy all", () => copyText(saved.map((c) => toPlain(c.text)).join("\n")), "rv-tbtn"),
			btn("Clear", () => { if (confirm("Clear the saved list?")) { saved = []; flag.set(writeJSON(KEY, saved)); renderSaved(); } }, "rv-tbtn rv-tbtn-danger"));
		savedWrap.appendChild(head);
		saved.forEach((c) => {
			const rowEl = el("div", "rv-cite-saved-item");
			const txt = el("div", "rv-cite-saved-text"); txt.innerHTML = toHtml(c.text, esc);
			const rm = btn("✕", () => { saved = saved.filter((x) => x.id !== c.id); flag.set(writeJSON(KEY, saved)); renderSaved(); }, "rv-fc-card-del");
			rm.setAttribute("aria-label", "Remove");
			rowEl.append(el("span", "rv-cite-saved-badge", c.style.toUpperCase()), txt, rm);
			savedWrap.appendChild(rowEl);
		});
	}

	styleSel.addEventListener("change", update);
	typeSel.addEventListener("change", buildFields);
	buildFields();
	renderSaved();
	s.appendChild(note("Citations are formatted for the common cases and stored only in this browser. Always double-check against your assignment's style guide."));
}
